import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import duomoArtImage from "../../img/el-milan-church-2.png";
import elLogo from "../../img/el-logo.svg";
import majorDamLogo from "../../img/majordam-logo-2.png";
import elTaglineEn from "../../img/el-tagline-en-2.png";
import elTaglineFr from "../../img/el-tagline-fr-2.png";
import type {
  Audience,
  GeneralContent,
  Locale,
  PublicBootstrapResponse,
  PublicItineraryResponse,
} from "../shared/types";

type AudienceChoice = Extract<Audience, "group1" | "group2">;

interface Preferences {
  locale: Locale | null;
  audience: AudienceChoice | null;
}

interface CopyBlock {
  languageOptions: Record<Locale, string>;
  audienceOptions: Record<AudienceChoice, string>;
  audienceBadges: Record<Audience, string>;
  itineraryButton: string;
  usefulLinksButton: string;
  usefulLinksTitle: string;
  usefulLinksEmpty: string;
  loading: string;
  refreshing: string;
  retry: string;
  errorTitle: string;
  errorBody: string;
  emptyTitle: string;
  emptyBody: string;
}

const STORAGE_KEY = "peakvision-public-preferences";

const copy: Record<Locale, CopyBlock> = {
  en: {
    languageOptions: {
      en: "English",
      fr: "Français",
    },
    audienceOptions: {
      group1: "Group 1",
      group2: "Group 2",
    },
    audienceBadges: {
      all: "All guests",
      group1: "Group 1",
      group2: "Group 2",
    },
    itineraryButton: "Itinerary",
    usefulLinksButton: "Useful links",
    usefulLinksTitle: "Useful links",
    usefulLinksEmpty: "Useful links will be added here soon.",
    loading: "Loading itinerary...",
    refreshing: "Refreshing itinerary...",
    retry: "Retry",
    errorTitle: "The itinerary could not be loaded",
    errorBody: "Please try again in a moment.",
    emptyTitle: "No itinerary available yet",
    emptyBody: "This trip has no published days for the selected group yet.",
  },
  fr: {
    languageOptions: {
      en: "English",
      fr: "Français",
    },
    audienceOptions: {
      group1: "Groupe 1",
      group2: "Groupe 2",
    },
    audienceBadges: {
      all: "Tous les participants",
      group1: "Groupe 1",
      group2: "Groupe 2",
    },
    itineraryButton: "Itinéraire",
    usefulLinksButton: "Liens utiles",
    usefulLinksTitle: "Liens utiles",
    usefulLinksEmpty: "Des liens utiles seront ajoutés ici sous peu.",
    loading: "Chargement de l'itinéraire...",
    refreshing: "Mise à jour de l'itinéraire...",
    retry: "Réessayer",
    errorTitle: "Impossible de charger l'itinéraire",
    errorBody: "Veuillez réessayer dans un instant.",
    emptyTitle: "Aucun itinéraire publié pour le moment",
    emptyBody: "Ce voyage n'a pas encore de journées publiées pour ce groupe.",
  },
};

function getInitialPreferences(): Preferences {
  if (typeof window === "undefined") {
    return { locale: null, audience: null };
  }

  const url = new URL(window.location.href);

  if (url.searchParams.has("reset")) {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }

    url.searchParams.delete("reset");
    const nextUrl = `${url.pathname}${url.search ? `?${url.searchParams.toString()}` : ""}${url.hash}`;
    window.history.replaceState({}, "", nextUrl);

    return { locale: null, audience: null };
  }

  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);

    if (rawValue) {
      const parsed = JSON.parse(rawValue) as Partial<Preferences>;
      return {
        locale: parsed.locale === "fr" || parsed.locale === "en" ? parsed.locale : null,
        audience: parsed.audience === "group1" || parsed.audience === "group2"
          ? parsed.audience
          : null,
      };
    }
  } catch {
    // Fall back to empty preferences.
  }

  return { locale: null, audience: null };
}

function formatTravelDate(dateValue: string, locale: Locale): string {
  try {
    return new Intl.DateTimeFormat(locale === "fr" ? "fr-CA" : "en-CA", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(new Date(`${dateValue}T00:00:00`));
  } catch {
    return dateValue;
  }
}

function formatNavDate(dateValue: string, locale: Locale): string {
  try {
    return new Intl.DateTimeFormat(locale === "fr" ? "fr-CA" : "en-CA", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(new Date(`${dateValue}T00:00:00`));
  } catch {
    return dateValue;
  }
}

function splitDescriptionLines(description: string): string[] {
  return description
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renderDescriptionWithLinks(
  text: string,
  rules: PublicItineraryResponse["links"],
): ReactNode {
  const preparedRules = [...rules]
    .filter((rule) => rule.replaceWhat && rule.url)
    .sort((left, right) => right.replaceWhat.length - left.replaceWhat.length);

  if (preparedRules.length === 0) {
    return text;
  }

  const dedupedRules = new Map<string, string>();
  for (const rule of preparedRules) {
    if (!dedupedRules.has(rule.replaceWhat)) {
      dedupedRules.set(rule.replaceWhat, rule.url);
    }
  }

  const matches = [...dedupedRules.keys()];

  if (matches.length === 0) {
    return text;
  }

  const matcher = new RegExp(`(${matches.map(escapeRegExp).join("|")})`, "g");
  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(matcher)) {
    const matchedText = match[0];
    const matchIndex = match.index ?? 0;

    if (matchIndex > lastIndex) {
      nodes.push(text.slice(lastIndex, matchIndex));
    }

    nodes.push(
      <a
        className="description-link"
        href={dedupedRules.get(matchedText)}
        key={`${matchedText}-${matchIndex}`}
        rel="noreferrer"
        target="_blank"
      >
        {matchedText}
      </a>,
    );

    lastIndex = matchIndex + matchedText.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : text;
}

function buildUsefulPdfPath(locale: Locale, fileName: string): string {
  return `/pdf/${locale}/${encodeURIComponent(fileName)}`;
}

function pickGeneralValue(
  general: GeneralContent | null,
  locale: Locale | null,
  frKey: keyof GeneralContent,
  enKey: keyof GeneralContent,
  fallback: string,
): string {
  if (!general) {
    return fallback;
  }

  if (locale === "fr") {
    return String(general[frKey]);
  }

  if (locale === "en") {
    return String(general[enKey]);
  }

  return `${general[frKey]} / ${general[enKey]}`;
}

function App() {
  const [preferences, setPreferences] = useState<Preferences>(getInitialPreferences);
  const [bootstrap, setBootstrap] = useState<GeneralContent | null>(null);
  const [itinerary, setItinerary] = useState<PublicItineraryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [activeDayIndex, setActiveDayIndex] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(74);
  const [dayNavHeight, setDayNavHeight] = useState(72);
  const headerRef = useRef<HTMLElement | null>(null);
  const dayCarouselRef = useRef<HTMLDivElement | null>(null);
  const daySectionRef = useRef<HTMLElement | null>(null);
  const usefulLinksSectionRef = useRef<HTMLElement | null>(null);
  const dayNavRef = useRef<HTMLElement | null>(null);

  const activeLocale = preferences.locale ?? "en";
  const strings = copy[activeLocale];
  const hasCompleteSelection = Boolean(preferences.locale && preferences.audience);
  const dayCount = itinerary?.days.length ?? 0;
  const showDayNav = Boolean(hasCompleteSelection && dayCount > 0);
  const activeDay = itinerary?.days[activeDayIndex] ?? null;
  const activeLinkRules = itinerary?.links ?? [];
  const activeUsefulLinks = itinerary?.usefulLinks ?? [];

  useEffect(() => {
    try {
      if (!preferences.locale && !preferences.audience) {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
      }
    } catch {
      // Ignore storage failures in private browsing or restricted environments.
    }
  }, [preferences]);

  useEffect(() => {
    document.documentElement.lang = preferences.locale ?? "en";
  }, [preferences.locale]);

  useEffect(() => {
    const pageEventTitle = itinerary?.general.eventTitle ?? bootstrap?.eventTitle ?? "Peak Vision Milan";
    const pageOrganizationName =
      itinerary?.general.organizationName ?? bootstrap?.organizationName ?? "EssilorLuxottica";

    document.title = `${pageEventTitle} | ${pageOrganizationName}`;
  }, [bootstrap, itinerary]);

  useEffect(() => {
    const controller = new AbortController();

    void fetch("/api/public/bootstrap", { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        return (await response.json()) as PublicBootstrapResponse;
      })
      .then((payload) => {
        setBootstrap(payload.general);
      })
      .catch((fetchError: unknown) => {
        if (fetchError instanceof Error && fetchError.name === "AbortError") {
          return;
        }

        setError(fetchError instanceof Error ? fetchError.message : "Unknown error");
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!hasCompleteSelection) {
      setLoading(false);
      setRefreshing(false);
      setItinerary(null);
      setActiveDayIndex(0);
      return;
    }

    const controller = new AbortController();
    const hasExistingData = itinerary !== null;

    if (hasExistingData) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError(null);

    void fetch(
      `/api/public/itinerary?locale=${preferences.locale}&audience=${preferences.audience}`,
      { signal: controller.signal },
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        return (await response.json()) as PublicItineraryResponse;
      })
      .then((payload) => {
        setItinerary(payload);
      })
      .catch((fetchError: unknown) => {
        if (fetchError instanceof Error && fetchError.name === "AbortError") {
          return;
        }

        setError(fetchError instanceof Error ? fetchError.message : "Unknown error");
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });

    return () => controller.abort();
  }, [
    hasCompleteSelection,
    preferences.audience,
    preferences.locale,
    reloadToken,
  ]);

  useEffect(() => {
    setActiveDayIndex(0);
    dayCarouselRef.current?.scrollTo({ left: 0, behavior: "auto" });
  }, [itinerary?.locale, itinerary?.days.length]);

  useEffect(() => {
    const carousel = dayCarouselRef.current;

    if (!carousel || !itinerary || itinerary.days.length === 0) {
      return;
    }

    const slides = Array.from(
      carousel.querySelectorAll<HTMLElement>("[data-day-index]"),
    );

    if (slides.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const activeEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0];

        if (!activeEntry) {
          return;
        }

        const nextIndex = Number(
          (activeEntry.target as HTMLElement).dataset.dayIndex ?? "0",
        );

        if (Number.isFinite(nextIndex)) {
          setActiveDayIndex(nextIndex);
        }
      },
      {
        root: carousel,
        threshold: [0.55, 0.75, 0.95],
      },
    );

    slides.forEach((slide) => observer.observe(slide));

    return () => observer.disconnect();
  }, [itinerary]);

  useEffect(() => {
    const updateHeights = () => {
      setHeaderHeight(headerRef.current?.offsetHeight ?? 74);
      setDayNavHeight(dayNavRef.current?.offsetHeight ?? 72);
    };

    updateHeights();

    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => updateHeights())
      : null;

    if (headerRef.current) {
      observer?.observe(headerRef.current);
    }

    if (dayNavRef.current) {
      observer?.observe(dayNavRef.current);
    }

    window.addEventListener("resize", updateHeights);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateHeights);
    };
  }, [showDayNav, hasCompleteSelection]);

  const eventTitle = itinerary?.general.eventTitle ?? bootstrap?.eventTitle ?? "Peak Vision Milan";
  const coverDate = hasCompleteSelection && itinerary
    ? itinerary.general.tripDateLabel
    : pickGeneralValue(
        bootstrap,
        preferences.locale,
        "tripDateLabelFr",
        "tripDateLabelEn",
        "27 avril au 2 mai 2026 / April 27 - May 2, 2026",
      );
  const coverDisclaimer = hasCompleteSelection && itinerary
    ? itinerary.general.disclaimer
    : pickGeneralValue(
        bootstrap,
        preferences.locale,
        "disclaimerFr",
        "disclaimerEn",
        "Les détails de l'itinéraire sont susceptibles d'être modifiés. / Itinerary details are subject to change.",
      );
  const organizationName = itinerary?.general.organizationName ?? bootstrap?.organizationName ?? "EssilorLuxottica";
  const welcomeDateFr = bootstrap?.tripDateLabelFr ?? "27 avril au 2 mai 2026";
  const welcomeDateEn = bootstrap?.tripDateLabelEn ?? "April 27 - May 2, 2026";
  const taglineImage = activeLocale === "fr" ? elTaglineFr : elTaglineEn;

  function scrollToDay(index: number) {
    const carousel = dayCarouselRef.current;

    if (!carousel || !itinerary || itinerary.days.length === 0) {
      return;
    }

    scrollToSection(daySectionRef.current);

    const clampedIndex = Math.min(Math.max(index, 0), itinerary.days.length - 1);
    const target = carousel.querySelector<HTMLElement>(`[data-day-index="${clampedIndex}"]`);

    if (!target) {
      return;
    }

    carousel.scrollTo({
      left: target.offsetLeft,
      behavior: "smooth",
    });
    setActiveDayIndex(clampedIndex);
  }

  function scrollToDaySection() {
    scrollToSection(daySectionRef.current);
  }

  function scrollToUsefulLinksSection() {
    scrollToSection(usefulLinksSectionRef.current);
  }

  function scrollToPageTop() {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  function scrollToSection(section: HTMLElement | null) {
    if (!section) {
      return;
    }

    const top = window.scrollY + section.getBoundingClientRect().top - headerHeight - 10;
    window.scrollTo({
      top: Math.max(top, 0),
      behavior: "smooth",
    });
  }

  const layoutStyle = {
    "--measured-header-height": `${hasCompleteSelection ? headerHeight : 0}px`,
    "--measured-footer-height": `${showDayNav ? dayNavHeight : 0}px`,
    "--page-bottom-padding": "0px",
  } as CSSProperties;

  return (
    <div className="public-page" style={layoutStyle}>
      <header
        aria-hidden={!hasCompleteSelection}
        className={`public-header ${hasCompleteSelection ? "is-visible" : "is-hidden"}`}
        ref={headerRef}
      >
        <div className="public-header-inner">
          <a className="public-logo" href="/?reset">
            <img alt={organizationName} className="public-logo-mark" src={elLogo} />
          </a>

          {hasCompleteSelection ? (
            <div className="public-header-controls">
              <HeaderSegmentedGroup
                value={preferences.locale!}
                onSelect={(locale) => setPreferences({ ...preferences, locale })}
                options={[
                  { label: "FR", value: "fr" },
                  { label: "EN", value: "en" },
                ]}
              />
              <HeaderSegmentedGroup
                value={preferences.audience!}
                onSelect={(audience) => setPreferences({ ...preferences, audience })}
                options={[
                  {
                    label:
                      activeLocale === "fr"
                        ? preferences.audience === "group1"
                          ? "Groupe 1"
                          : "1"
                        : preferences.audience === "group1"
                          ? "Group 1"
                          : "1",
                    value: "group1",
                  },
                  {
                    label:
                      activeLocale === "fr"
                        ? preferences.audience === "group2"
                          ? "Groupe 2"
                          : "2"
                        : preferences.audience === "group2"
                          ? "Group 2"
                          : "2",
                    value: "group2",
                  },
                ]}
              />
            </div>
          ) : null}
        </div>
      </header>

      <div
        className={`public-inner ${hasCompleteSelection ? "" : "is-welcome"} ${showDayNav ? "has-day-nav" : ""}`}
      >
        <div className={`welcome-brand-shell ${hasCompleteSelection ? "is-hidden" : ""}`}>
          <img alt={organizationName} className="welcome-brand-mark" src={elLogo} />
        </div>

        <main className="itinerary-shell">
          <section className={`cover-card ${hasCompleteSelection ? "" : "is-welcome"}`}>
            <DuomoLineArt />

            {hasCompleteSelection ? (
              <div className="cover-copy">
                <h1>{eventTitle}</h1>
                <p className="cover-date">{coverDate}</p>
                <p className="cover-disclaimer">{coverDisclaimer}</p>
              </div>
            ) : (
              <div className="cover-copy cover-copy-welcome">
                <h1>{eventTitle}</h1>
                <div className="cover-date-stack">
                  <p className="cover-date">{welcomeDateFr}</p>
                  <p className="cover-date">{welcomeDateEn}</p>
                </div>

                <section className="chooser-card">
                  <div className="chooser-block">
                    <p className="chooser-line">Votre langue - Your language</p>
                    <div className="selector-row chooser-row">
                      <button
                        className={`selector-chip ${preferences.locale === "fr" ? "is-active" : ""}`}
                        onClick={() => setPreferences({ ...preferences, locale: "fr" })}
                        type="button"
                      >
                        Français
                      </button>
                      <button
                        className={`selector-chip ${preferences.locale === "en" ? "is-active" : ""}`}
                        onClick={() => setPreferences({ ...preferences, locale: "en" })}
                        type="button"
                      >
                        English
                      </button>
                    </div>
                  </div>

                  <div className="chooser-block">
                    <p className="chooser-line">Votre groupe - Your group</p>
                    <div className="selector-row chooser-row">
                      <button
                        className={`selector-chip ${preferences.audience === "group1" ? "is-active" : ""}`}
                        onClick={() => setPreferences({ ...preferences, audience: "group1" })}
                        type="button"
                      >
                        1
                      </button>
                      <button
                        className={`selector-chip ${preferences.audience === "group2" ? "is-active" : ""}`}
                        onClick={() => setPreferences({ ...preferences, audience: "group2" })}
                        type="button"
                      >
                        2
                      </button>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {hasCompleteSelection ? (
              <img alt="" aria-hidden="true" className="cover-tagline" src={taglineImage} />
            ) : null}

            {showDayNav ? (
              <div className="cover-jump-actions">
                <button
                  className="cover-jump-button"
                  onClick={scrollToDaySection}
                  type="button"
                >
                  {strings.itineraryButton}
                </button>
                <button
                  className="cover-jump-button"
                  onClick={scrollToUsefulLinksSection}
                  type="button"
                >
                  {strings.usefulLinksButton}
                </button>
              </div>
            ) : null}
          </section>

          {hasCompleteSelection && loading && !itinerary ? (
            <section className="status-card">
              <div className="spinner public-spinner" />
              <p>{strings.loading}</p>
            </section>
          ) : null}

          {hasCompleteSelection && error && !itinerary ? (
            <section className="status-card error-card">
              <h2>{strings.errorTitle}</h2>
              <p>{strings.errorBody}</p>
              <button
                className="selector-chip is-active"
                onClick={() => setReloadToken((value) => value + 1)}
                type="button"
              >
                {strings.retry}
              </button>
            </section>
          ) : null}

          {hasCompleteSelection && itinerary ? (
            <>
              {itinerary.days.length > 0 ? (
                <section className="day-section" ref={daySectionRef}>
                  <div className="day-carousel-shell">
                    <div className="day-carousel" ref={dayCarouselRef}>
                      {itinerary.days.map((day, index) => (
                        <article
                          className="day-slide"
                          data-day-index={index}
                          key={day.id}
                        >
                          <div className="day-card">
                            <header className="day-card-header">
                              <div>
                                <p className="day-date">
                                  {formatTravelDate(day.travelDate, preferences.locale!)}
                                </p>
                                <h2>{day.title}</h2>
                              </div>
                              <span className="audience-pill">
                                {strings.audienceBadges[day.audience]}
                              </span>
                            </header>

                            <div className="segment-stack">
                              {day.segments.map((segment) => (
                                <article className="segment-card-public" key={segment.id}>
                                  <p className="segment-time">{segment.timeLabel}</p>
                                  <div className="segment-body">
                                    <h3>{segment.title}</h3>
                                    {segment.description ? (
                                      (() => {
                                        const descriptionLines = splitDescriptionLines(segment.description);

                                        if (descriptionLines.length <= 1) {
                                          return (
                                            <p>
                                              {renderDescriptionWithLinks(
                                                descriptionLines[0] ?? segment.description,
                                                activeLinkRules,
                                              )}
                                            </p>
                                          );
                                        }

                                        return (
                                          <ul className="segment-description-list">
                                            {descriptionLines.map((line) => (
                                              <li key={line}>
                                                {renderDescriptionWithLinks(line, activeLinkRules)}
                                              </li>
                                            ))}
                                          </ul>
                                        );
                                      })()
                                    ) : null}
                                  </div>
                                </article>
                              ))}
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>

                  {showDayNav && activeDay ? (
                    <nav className="day-nav" aria-label="Day navigation" ref={dayNavRef}>
                      <button
                        aria-label="Previous day"
                        className="day-nav-button"
                        disabled={activeDayIndex === 0}
                        onClick={() => scrollToDay(activeDayIndex - 1)}
                        type="button"
                      >
                        ←
                      </button>
                      <button
                        aria-label="Back to top"
                        className="day-nav-button day-nav-button-top"
                        onClick={scrollToPageTop}
                        type="button"
                      >
                        ↑
                      </button>
                      <button
                        aria-label="Next day"
                        className="day-nav-button"
                        disabled={activeDayIndex === dayCount - 1}
                        onClick={() => scrollToDay(activeDayIndex + 1)}
                        type="button"
                      >
                        →
                      </button>
                    </nav>
                  ) : null}
                </section>
              ) : (
                <section className="status-card">
                  <h2>{strings.emptyTitle}</h2>
                  <p>{strings.emptyBody}</p>
                </section>
              )}

              <section className="useful-links-section" ref={usefulLinksSectionRef}>
                <h2>{strings.usefulLinksTitle}</h2>
                {activeUsefulLinks.length > 0 ? (
                  <div className="useful-links-list">
                    {activeUsefulLinks.map((link) => (
                      <a
                        className="useful-link-item"
                        href={buildUsefulPdfPath(activeLocale, link.fileName)}
                        key={link.id}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {link.title}
                      </a>
                    ))}
                  </div>
                ) : (
                  <p>{strings.usefulLinksEmpty}</p>
                )}
                <div className="majordam">
                  <a href="https://majordam.com" target="_blank" rel="noreferrer">
                    Powered by
                    <br />
                    <img src={majorDamLogo} alt="MajorDam" />
                  </a>
                </div>
              </section>
            </>
          ) : null}
        </main>
      </div>
    </div>
  );
}

function HeaderSegmentedGroup<T extends string>(props: {
  value: T;
  onSelect: (value: T) => void;
  options: { label: string; value: T }[];
}) {
  return (
    <div className="header-pill">
      {props.options.map((option) => (
        <button
          key={option.value}
          className={`header-pill-option ${props.value === option.value ? "is-active" : ""}`}
          onClick={() => props.onSelect(option.value)}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function DuomoLineArt() {
  return (
    <img
      className="duomo-art"
      src={duomoArtImage}
      alt=""
      aria-hidden="true"
    />
  );
}

export default App;
